<div class="grid grid-nogutter w-full h-screen login-container relative overflow-hidden m-0"
  [class.rtl]="languageService.currentLanguage() === 'ar'">

  <!-- Global Loading Indicator -->
  @if (loading) {
  <div class="absolute top-0 left-0 w-full z-50">
    <p-progressBar mode="indeterminate" [style]="{ height: '4px' }" color="#f07818"></p-progressBar>
  </div>
  }

  <!-- Language Switcher in Corner -->
  <div class="absolute top-0 right-0 p-4 z-5 flex gap-2">
    <button pButton type="button" label="EN" class="p-button-text p-button-sm text-white"
      [class.font-bold]="languageService.currentLanguage() === 'en'"
      (click)="languageService.setLanguage('en')"></button>
    <div class="border-left-1 border-gray-700 h-1rem mt-2"></div>
    <button pButton type="button" label="عربي" class="p-button-text p-button-sm text-white"
      [class.font-bold]="languageService.currentLanguage() === 'ar'"
      (click)="languageService.setLanguage('ar')"></button>
  </div>

  <!-- LEFT COLUMN: Image Hero -->
  <div class="hidden md:flex col-12 md:col-6 relative overflow-hidden bg-no-repeat bg-cover p-0"
    style="background-image: url('assets/images/loadgate-dark-hero.png'); background-position: center;">
    <div class="absolute top-0 left-0 w-full h-full bg-black-alpha-40 z-0"></div>

    <div class="absolute top-0 left-0 p-5 z-1">
      <div class="flex align-items-center gap-2">
        <img src="assets/images/loadgate-new-logo.png" alt="LoadGate" class="block w-auto" style="height: 128px" />
        <span class="text-3xl font-bold text-white tracking-widest">{{ 'APP.NAME' | translate }}</span>
      </div>
    </div>

    <div class="absolute bottom-0 left-0 w-full p-6 z-1 mb-4">
      <h1 class="text-4xl text-white font-bold mb-2">{{ 'APP.SLOGAN' | translate }}</h1>
      <div class="flex gap-2 mt-4">
        <div class="w-2rem h-4px bg-white border-round"></div>
        <div class="w-2rem h-4px bg-white-alpha-30 border-round"></div>
        <div class="w-2rem h-4px bg-white-alpha-30 border-round"></div>
      </div>
    </div>
  </div>

  <!-- RIGHT COLUMN: Form -->
  <div
    class="col-12 md:col-6 flex flex-column justify-content-center px-4 py-4 md:p-6 bg-gray-900 text-white relative h-full overflow-y-auto no-scrollbar">
    <div class="w-full mx-auto" style="max-width: 450px;">

      <div class="mb-4">
        <h2 class="text-3xl font-semibold text-white mb-1">{{ 'LOGIN.TITLE' | translate }}</h2>
        <p class="text-gray-400 text-sm">{{ 'LOGIN.SUBTITLE' | translate }}</p>
      </div>

      <!-- Messages -->
      @if (errorMessage) {
      <div
        class="bg-red-900 border-1 border-red-700 text-red-100 p-3 border-round mb-4 text-sm flex align-items-start gap-2">
        <i class="pi pi-exclamation-circle mt-1"></i>
        <span>{{ errorMessage }}</span>
      </div>
      }
      @if (infoMessage) {
      <div
        class="bg-blue-900 border-1 border-blue-700 text-blue-100 p-3 border-round mb-4 text-sm flex align-items-start gap-2">
        <i class="pi pi-info-circle mt-1"></i>
        <span>{{ infoMessage }}</span>
      </div>
      }

      <form [formGroup]="loginForm" (ngSubmit)="onSubmit()" class="flex flex-column gap-4" novalidate>
        @if (phoneStep === 'idle') {
        <!-- Email -->
        <span class="p-float-label">
          <input pInputText id="email" type="email" formControlName="email" class="w-full login-input"
            [class.ng-invalid]="loginForm.get('email')?.invalid && loginForm.get('email')?.touched" />
          <label for="email">{{ 'LOGIN.EMAIL' | translate }}</label>
        </span>
        @if (loginForm.get('email')?.touched && loginForm.get('email')?.invalid) {
        <small class="text-red-400 -mt-3 block">Valid email is required.</small>
        }

        <!-- Password -->
        <span class="p-float-label mt-2">
          <input pInputText id="password" type="password" formControlName="password" class="w-full login-input" />
          <label for="password">{{ 'LOGIN.PASSWORD' | translate }}</label>
        </span>

        <div class="flex justify-content-between align-items-center">
          <div class="flex align-items-center gap-2 font-bold">
            <p-checkbox formControlName="rememberMe" [binary]="true" inputId="remember"
              styleClass="dark-checkbox"></p-checkbox>
            <label for="remember" class="text-sm cursor-pointer text-gray-300">{{ 'LOGIN.REMEMBER_ME' | translate
              }}</label>
          </div>
          <a (click)="onForgotPassword()"
            class="text-sm font-medium text-purple-400 cursor-pointer hover:text-purple-300 transition-colors">{{
            'LOGIN.FORGOT_PASSWORD' | translate }}</a>
        </div>

        <button pButton pRipple type="submit" [label]="'LOGIN.BUTTON' | translate"
          class="w-full font-bold custom-btn-primary p-3"></button>
        }

        <!-- Phone Step -->
        @if (phoneStep === 'sending') {
        <div class="flex flex-column gap-3">
          <div class="flex align-items-center justify-content-between">
            <h3 class="m-0 text-lg font-bold text-white">{{ (usesWhatsAppOtp ? 'LOGIN.WHATSAPP_OTP_TITLE' : 'LOGIN.OTP_TITLE') | translate }}</h3>
            <button pButton icon="pi pi-times" class="p-button-text text-gray-400 p-button-rounded p-button-sm"
              (click)="cancelPhoneAuth()"></button>
          </div>
          <span class="p-float-label">
            <input pInputText id="phone" type="tel" formControlName="phone" class="w-full login-input" />
            <label for="phone">{{ 'LOGIN.PHONE_LABEL' | translate }}</label>
          </span>
          <button pButton [label]="'LOGIN.OTP_SEND' | translate" type="button" class="w-full custom-btn-primary"
            (click)="sendPhoneCode()" [loading]="loading"></button>
        </div>
        }

        @if (phoneStep === 'code-sent' || phoneStep === 'verifying') {
        <div class="flex flex-column gap-3">
          <div class="flex align-items-center justify-content-between">
            <h3 class="m-0 text-lg font-bold text-white">{{ (usesWhatsAppOtp ? 'LOGIN.WHATSAPP_OTP_TITLE' : 'LOGIN.OTP_TITLE') | translate }}</h3>
            <button pButton icon="pi pi-times" class="p-button-text text-gray-400 p-button-rounded p-button-sm"
              (click)="cancelPhoneAuth()"></button>
          </div>
          <div class="bg-gray-800 p-3 border-round text-sm text-gray-300 mb-2 border-1 border-gray-700">
            {{ 'LOGIN.OTP_SENT_TO' | translate }} <strong class="text-white">{{ loginForm.get('phone')?.value
              }}</strong>
          </div>
        </div>
        }
      </form>

      @if (phoneStep === 'code-sent' || phoneStep === 'verifying') {
      <form [formGroup]="otpForm" (ngSubmit)="verifyPhoneCode()" class="mt-3 flex flex-column gap-3">
        <span class="p-float-label">
          <input pInputText id="otp" formControlName="code"
            class="w-full text-center text-xl tracking-widest font-mono login-input" maxlength="6" />
          <label for="otp">{{ 'LOGIN.OTP_CODE' | translate }}</label>
        </span>
        <button pButton type="submit" [label]="'LOGIN.OTP_VERIFY' | translate" class="w-full custom-btn-primary"
          [loading]="phoneStep === 'verifying'" [disabled]="otpForm.invalid"></button>
        <button pButton type="button" [label]="'LOGIN.OTP_RESEND' | translate"
          class="p-button-text w-full text-gray-400" (click)="resendOtp()"></button>
      </form>
      }

      @if (phoneStep === 'idle') {
      <div class="flex align-items-center gap-3 my-4">
        <div class="flex-1 border-top-1 border-gray-700"></div>
        <span class="text-gray-500 text-xs font-medium uppercase">{{ 'LOGIN.OR_LOGIN_WITH' | translate }}</span>
        <div class="flex-1 border-top-1 border-gray-700"></div>
      </div>

      <div class="w-full" style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem;">
        @for (option of signInOptions(); track option.action) {
        <button pButton type="button"
          class="custom-btn-outlined w-full justify-content-center p-2 white-space-nowrap overflow-hidden"
          (click)="signInWith(option)" [disabled]="loading" [pTooltip]="option.labelKey | translate"
          tooltipPosition="top">
          <span class="flex align-items-center gap-2">
            <fa-icon [icon]="option.icon"></fa-icon>
            <span class="font-medium text-overflow-ellipsis overflow-hidden">{{ option.labelKey | translate }}</span>
          </span>
        </button>
        }
      </div>
      <div class="text-center mt-4 mb-2">
        <a routerLink="/register"
          class="font-bold text-white no-underline hover:text-purple-400 text-sm transition-colors">{{
          'LOGIN.GO_TO_REGISTER' | translate }}</a>
      </div>
      }

      <!-- Recaptcha Container -->
      <div [id]="recaptchaContainerId"></div>
    </div>
  </div>
</div>
